import React, { useMemo } from 'react';
import {
  BarChartIcon,
  CalendarIcon,
  EnvelopeClosedIcon,
  ExclamationTriangleIcon,
  PersonIcon,
  ReaderIcon,
  Share1Icon,
} from '@radix-ui/react-icons';
import { Select, Spinner } from '../../../../components';
import type { SelectOption } from '../../../../components';
import { TaskStatus } from '../../../../types/kanban';
import { useKanbanStore } from '../../../../store/kanbanStore';
import { TaskFormData } from '../types';
import { PRIORITY_OPTIONS, STATUS_OPTIONS } from '../constants';
import { SidebarField } from './SidebarField';
import { TaskLabelsField } from './TaskLabelsField';
import { TaskLeadField } from './TaskLeadField';

interface TaskSidebarProps {
  formData: TaskFormData;
  canEditLead: boolean;
  onChange: <K extends keyof TaskFormData>(field: K, value: TaskFormData[K]) => void;
  onLeadClick?: () => void;
  onLeadMetaResolved?: (meta: {
    leadUniqueId?: string | null;
    leadUserId?: string | null;
  }) => void;
  onSave: () => void;
  isSaving?: boolean;
  isValid?: boolean;
  saveLabel?: string;
  readOnly?: boolean;
}

export const TaskSidebar: React.FC<TaskSidebarProps> = ({
  formData,
  canEditLead,
  onChange,
  onLeadClick,
  onLeadMetaResolved,
  onSave,
  isSaving = false,
  isValid = true,
  saveLabel = 'Save Changes',
  readOnly = false,
}) => {
  const { assignees, reporters, taskTypes } = useKanbanStore();

  const assigneeOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: 'Unassigned' },
      ...assignees.map((a) => ({
        value: String(a.userId),
        label: a.name,
      })),
    ],
    [assignees]
  );

  const reporterOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: 'None' },
      ...reporters.map((r) => ({
        value: String(r.userId),
        label: r.name,
      })),
    ],
    [reporters]
  );

  const typeOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: 'None' },
      ...taskTypes.map((t) => ({
        value: String(t.id),
        label: t.name,
      })),
    ],
    [taskTypes]
  );

  return (
    <aside className="task-details-modal__sidebar">
      <div className="task-details-modal__card task-details-modal__sidebar-card">
        <TaskLeadField
          leadId={formData.leadId}
          leadUniqueId={formData.leadUniqueId}
          leadUserId={formData.leadUserId}
          canEditLead={canEditLead && !readOnly}
          onLeadClick={onLeadClick}
          onLeadMetaResolved={onLeadMetaResolved}
          onChange={(value, nextLeadUniqueId, nextLeadUserId) => {
            onChange('leadId', value);
            onChange('leadUniqueId', nextLeadUniqueId ?? '');
            onChange('leadUserId', nextLeadUserId ?? '');
          }}
        />

        <SidebarField label="Assign To" icon={<PersonIcon aria-hidden="true" />}>
          <Select
            value={formData.assigneeId}
            onChange={(v) => onChange('assigneeId', v)}
            options={assigneeOptions}
            searchable={false}
            disabled={readOnly}
            className="task-details-modal__select"
          />
        </SidebarField>

        <SidebarField label="Reporter" icon={<ReaderIcon aria-hidden="true" />}>
          <Select
            value={formData.reporterId}
            onChange={(v) => onChange('reporterId', v)}
            options={reporterOptions}
            searchable={false}
            disabled={readOnly}
            className="task-details-modal__select"
          />
        </SidebarField>

        <TaskLabelsField
          labelIds={formData.labelIds}
          onChange={(labelIds) => onChange('labelIds', labelIds)}
          disabled={readOnly}
        />

        <SidebarField label="Type" icon={<Share1Icon aria-hidden="true" />}>
          <Select
            value={formData.taskTypeId}
            onChange={(v) => onChange('taskTypeId', v)}
            options={typeOptions}
            searchable={false}
            disabled={readOnly}
            className="task-details-modal__select"
          />
        </SidebarField>

        <SidebarField label="Due Date" icon={<CalendarIcon aria-hidden="true" />}>
          <input
            type="date"
            className="task-details-modal__date-input"
            value={formData.dueDate}
            onChange={(e) => onChange('dueDate', e.target.value)}
            disabled={readOnly}
            readOnly={readOnly}
          />
        </SidebarField>

        <SidebarField
          label="Priority"
          icon={<ExclamationTriangleIcon aria-hidden="true" />}
        >
          <Select
            value={formData.priority}
            onChange={(v) => onChange('priority', v)}
            options={PRIORITY_OPTIONS}
            searchable={false}
            disabled={readOnly}
            className="task-details-modal__select"
          />
        </SidebarField>

        <SidebarField label="Status" icon={<BarChartIcon aria-hidden="true" />}>
          <Select
            value={formData.columnId}
            onChange={(v) => onChange('columnId', v as TaskStatus)}
            options={STATUS_OPTIONS}
            searchable={false}
            disabled={readOnly}
            className="task-details-modal__select task-details-modal__select--status"
          />
        </SidebarField>

        {!readOnly && (
        <button
          type="button"
          className="task-details-modal__save"
          onClick={onSave}
          disabled={isSaving || !isValid}
        >
          {isSaving ? <Spinner size="small" /> : null}
          {isSaving ? 'Saving...' : saveLabel}
        </button>
        )}
      </div>
    </aside>
  );
};
